import { CustomException, util, elk, language } from '@tencent/ppd-base-libs';
import { cloneDeep } from 'lodash-es';
import { getCvData2022 } from '../../api/index';
import { GovCVData, Stage, RegistState, CVCardInfo, StepInfo, GovCVData2022, GovCVDataRes2022 } from '../../type';
import { i18nGet } from '../../i18n';
import { CStorageKeyFirstRegisted, CStorageKeyFirstApproved, CStorageKeyGovFirstSendSucc, CStorageKeyGovFirstSendTransfer, CStorageKeyGovFirstSendUndo, EggActFirstkey } from '../../config';
import { isNDaysLater, getExpireTimeTips, to } from '../../utils/index';
import { GOV_LIST_2022 } from '../../api/index';

let cvData: GovCVDataRes2022|null = null;

let promiseGetGovCvRequest: Promise<GovCVDataRes2022> | null = null;

// promise缓存，避免并发同时2次调用接口
async function promiseGetGovCvData() {
  if (promiseGetGovCvRequest) {
    return promiseGetGovCvRequest;
  }
  promiseGetGovCvRequest = getCvData2022();
  return promiseGetGovCvRequest;
}

/** 获取具体数据 */
export function getData(cvId?: string) {
  const {  allList } = cvData as GovCVDataRes2022;
  return allList?.find(item => item.cvId === cvId);
}

/** 获取具体数据 */
export function getState(cvId?: string) {
  const voucherItem = getData(cvId);
  if (cvData?.govData.state === RegistState.sendSucc
    && (voucherItem?.cvState === RegistState.sendUndo
      || voucherItem?.cvState === RegistState.sendTransfer
      || voucherItem?.cvState === RegistState.overdue)) {
    return voucherItem?.cvState;
  }
  return cvData?.govData.state;
}

/**
 * 获取政府消费券数据
 * @param refresh 是否刷新数据，不使用缓存
 * @returns
 */
export async function getCVData(
  refresh = false,
  isFreeze = false,
): Promise<GovCVDataRes2022> {
  if (refresh) {
    // 如果刷新信息，promise缓存清空
    promiseGetGovCvRequest = null;
    cvData = null;
  }
  if (isFreeze) {
    cvData = getFreezeCVData();
    return cvData;
  }

  if (cvData) {
    return cvData;
  }
  const [err, res] = await to(promiseGetGovCvData());
  if (err) {
    throw err;  // 由外部处理
    // throw new CustomException(err, 'GovCVInitException', 'error in gov-cv/business.ts getCVData()', {
    //   // @ts-ignore
    //   alarmCfg: {
    //     level: '1min_1times',
    //   },
    // });
  }
  cvData = res;

  // 上报多维度的数据，方便线上监控
  elk({
    key: 'gov-cv-data',
    str1: cvData?.govData.govCouponStage,
    str2: cvData?.govData.state,
    str3: cvData?.govData.dispatchFailReason,
    msg: JSON.stringify(cvData?.govData),
  });
  return cvData;
}

/**
 * 获取预热阶段，政府消费券的数据
 * 这里不需要从接口获取，直接写死初始化展示的数据
 */
// export function getPreRegStageCVData(): GovCVData {
//   cvData = {
//     govCouponStage: Stage.preRegist,
//     state: RegistState.unRegist,
//     couponBalance: '5000.00',
//   };
//   return cvData;
// }

/**
 * 冻结用户的展示数据
 * 这里不需要从接口获取，直接写死初始化展示的数据
 */
export function getFreezeCVData(): GovCVDataRes2022 {
  return {
    govData: {
      govCouponStage: +Stage.endRegist,
      enhancedUid: '*** *** ****',
      state: RegistState.unRegist,
      couponBalance: '5000.00',
    },
    voucherItemList: [],
    allList: [],
    expiredVoucherItemList: [],
  };
}

export const faqUrl = 'https://kf.qq.com/touch/product/hk_wallet_app.html?scene_id=kf3420#showmenu=1006964';
// export const gHkGovRegUrl = 'https://www.consumptionvoucher.gov.hk/tc/register.html';
export const gHkGovRegUrl = 'https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_map_index.fcgi?page=2hkgov'; // 不要随便改，调用时额外拼接其他参数了

function getCvName(cvId) {
  if (GOV_LIST_2022.includes(cvId)) {
    return i18nGet('2022政府消费券 (I)');
  }
  return i18nGet('政府消费券');
}

const lang = language.getLanguage();
export const dealUrl = lang === 'en'
  ? 'https://kf.qq.com/touch/sappfaq/210730jAJba2210730Yb6zm6.html?scene_id=kf3418&platform=15'
  : 'https://kf.qq.com/touch/sappfaq/210730EzeAzy210730BBrYRB.html?scene_id=kf3420&platform=15'
  ;
export function getCVCard(cvId = ''): CVCardInfo {
  const card: CVCardInfo = {
    isInUse: isInUse(cvId),
    isInWait: isInWait(),
    isInFail: isGovCVInvalidBeforeSend(),
    isSendFail: isSendFail(),
    isInExceptions: isGovCVInvalidAfterSend(cvId),
    isOverdue: isOverdue(cvId),
    isLock: isLock(),
    isFirstState: isFirstState(),
    logo: 'hkgov',
    cvName: getCvName(cvId),
    desc: getDesc(cvId),
    multiDesc: getMultiDesc(cvId),
    stateTips: getStateTips(cvId),
    balance: getBalance(cvId),
    steps: getStep(cvId),
    releaseTips: getReleaseTips(cvId),
  };
  return card;
}

function isFirstState() {
  const state = cvData?.govData.state || '';
  if (!state) {
    return false;
  }
  const key = `is_first_state${state}`;
  const val = localStorage.getItem(key);
  localStorage.setItem(key, '1');
  return !!val;
}

function getBalance(cvId: string) {
  const voucherItem = getData(cvId);
  if (isLock()) {  // 券发放成功之前都展示锁着的5000
    return '5000';
  }
  return voucherItem?.balance || cvData?.govData.couponBalance || '0.00';
}

// 卡面展示
function getDesc(cvId: string) {
  let desc = '';
  if (isPreRegStage() || isUnRegist() || isInWait()) {
    desc = '';
  } else {
    desc = getStateTips(cvId);
  }
  return desc;
}

function getMultiDesc(cvId: string) {
  const voucherItem = getData(cvId);
  if (!voucherItem) {
    return '';
  }
  let desc = '';
  const { cvState } = voucherItem;
  switch (cvState) {
    case RegistState.sendUndo:
      desc = i18nGet('根据政府通知，您的政府消费券已被撤销，资金将退回政府，该券已失效');
      break;
    case RegistState.sendTransfer:
      desc = i18nGet('根据政府通知，您的政府消费券已被转移至新账户');
      break;
    default:
      break;
  }
  return desc;
}

function isLock() {
  return isPreRegStage() || isUnRegist()
  || isInWait() || isGovCVInvalidBeforeSend() || isSendFail();
}

/**
 * 是否预热阶段
 */
export function isPreRegStage() {
  return cvData?.govData.govCouponStage === Stage.preRegist;
}

/**
 * 是否登记阶段
 */
export function isRegistStage() {
  return cvData?.govData.govCouponStage === Stage.regist;
}

/**
 * 是否未登记
 */
export function isUnRegist() {
  return cvData?.govData.state === RegistState.unRegist;
}

/**
 * 是否已登记
 */
export function isRegisted() {
  return cvData?.govData.state === RegistState.registed;
}

/**
 * 是否审核通过
 */
export function isApproved() {
  return cvData?.govData.state === RegistState.approved;
}

/**
 * 是否等待派发中,已登记/审核通过
 */
export function isInWait() {
  return isRegisted() || isApproved();
}
/**
 * 是否过期
*/
export function isExpired(cvId?: string) {
  const state = cvId ? getState(cvId) : cvData?.govData.state;
  return state === RegistState.overdue;
}
/**
 * 终态
 */
export function isCvsEnd(cvId?: string) {
  const state = cvId ? getState(cvId) : cvData?.govData.state;
  return [
    RegistState.undo,
    RegistState.transfer,
    RegistState.sendUndo,
    RegistState.sendTransfer,
  ].includes(state as any);
}

/**
 * 派券成功 及 以后 的状态
 */
export function isCvSended(cvId?: string) {
  const state = cvId ? getState(cvId) : cvData?.govData.state;
  return [
    RegistState.sendSucc,
    RegistState.sendUndo,
    RegistState.sendTransfer,
    RegistState.overdue,
  ].includes(state as any);
}

export function isSendFail() {
  return cvData?.govData.state === RegistState.sendFail;
}

/**
 * 是否使用中
 */
export function isInUse(cvId?: string) {
  const voucherItem = getData(cvId);
  const state = voucherItem ? voucherItem.cvState : cvData?.govData.state;
  return state === RegistState.sendSucc;
}

export function getRegState() {
  return cvData?.govData.state;
}

export function isEndRegist() {
  return cvData?.govData.govCouponStage === Stage.endRegist;
}

/**
 * 是否部分撤销
 */
export function isRelease(cvId: string) {
  const voucherItem = getData(cvId);
  return isInUse(cvId) && (voucherItem?.releaseAmount || 0) > 0 && !isCloseReleaseTips();
}

/**
 * 是否部分撤销状态
 */
export function isReleaseStatus(cvId: string) {
  const voucherItem = getData(cvId);
  return isInUse(cvId) && (voucherItem?.releaseAmount || 0) > 0;
}

function isCloseReleaseTips() {
  const val = localStorage.getItem('hasCloseReleaseTips');
  return val === '1';
}

export function saveCloseReleaseTips() {
  localStorage.setItem('hasCloseReleaseTips', '1');
}

export function getReleaseTips(cvId: string) {
  const voucherItem = getData(cvId);
  let tips = '';
  if (isRelease(cvId)) {
    const amount = voucherItem?.releaseAmount || 0;
    tips = i18nGet('根据政府通知，已撤销您政府消费券资金HK${{amount}},并将退回政府。', {
      // amount: cvData?.releaseAmount || 0,
      amount,
    });
  }
  return tips;
}

export function getStateTips(cvId?: string) {
  let voucherItem;
  if (cvId) {
    voucherItem = getData(cvId);
  }
  let tips = '';
  const state = voucherItem ? voucherItem.cvState : cvData?.govData.state;
  // if (voucherItem && cvData?.govData.state === RegistState.sendTransfer
  //   && voucherItem.cvState === RegistState.overdue) {
  //   state = voucherItem.cvState;
  // }
  switch (state) {
    case RegistState.unRegist:
      tips = i18nGet('登记成功后分两期派发');
      break;
    case RegistState.registed:
      tips = i18nGet('您的登记申请已提交，请留意政府审核通知');
      break;
    case RegistState.approved:
      tips = i18nGet('政府审核通过，等待消费券下发');
      break;
    case RegistState.undo:
      tips = i18nGet('您的政府消费券登记已被取消');
      break;
    case RegistState.transfer:
      tips = i18nGet('您的政府消费券登记账户已变更');
      break;
    case RegistState.sendFail:
      // tips = i18nGet('您的政府消费券发放失败');
      break;
    case RegistState.sendSucc:
      tips = getExpireTimeTips(voucherItem?.expireTime);
      break;
    case RegistState.sendUndo:
      tips = i18nGet('已撤销');
      break;
    case RegistState.sendTransfer:
      tips = i18nGet('已转移');
      break;
    case RegistState.overdue:
      tips = i18nGet('已过期');
      break;
    default:
      break;
  }
  return tips;
}

function getFailTips() {
  if (!cvData?.govData) {
    return '';
  }
  let tips = '';
  const { state }  = cvData?.govData;
  switch (state) {
    case RegistState.undo:
      tips = i18nGet('根据政府通知，您的消费券登记已被取消，本账户后续将不再接收消费券，也无法继续参与消费券相关优惠活动。');
      break;
    case RegistState.transfer:
      tips = i18nGet('根据政府通知，您已将消费券登记账户转移为另一WeChat Pay HK账户，本账户后续将不再接收消费券，也无法继续参与消费券相关的优惠活动。');
      break;
    case RegistState.sendFail:
      tips = getFailReasonTips();
      break;
    default:
      break;
  }
  return tips;
}

function getFailReasonTips() {
  const reasonNum = cvData?.govData.dispatchFailReason;
  const Map = {
    51: i18nGet('因您的账户异常，消费券派发失败，请及时联系政府有关部门处理'),
  };
  if (!reasonNum || !Map[reasonNum]) {
    return i18nGet('消费券派发失败，请及时联系政府有关部门处理');
  }
  return Map[reasonNum];
}

function getStep(cvId: string) {
  let steps: StepInfo[] = [];
  if (isInWait()) {
    const laterTips = getNDaysLaterTips();
    steps = [
      {
        // type: getStepType(RegistState.registed),
        type: '',
        txt: getStateTips(cvId),
        desc: laterTips,
        descLinkName: laterTips ? i18nGet('去消费券计划网站') : '',
        descLink: laterTips ? gHkGovRegUrl : '',
      },
      // {
      //   type: getStepType(RegistState.approved),
      //   txt: getStateTips(RegistState.approved),
      // },
    ];
  } else if (isGovCVInvalidBeforeSend()) {
    const failTipsObj = getFailTipsObj();
    steps = [failTipsObj];
  }
  return steps;
}

export function getFailTipsObj() {
  const { name, link } = getFailLinkObj();
  return {
    type: 'warn',
    txt: getStateTips(),
    desc: getFailTips(),
    descLinkName: name,
    descLink: link,
  };
}

function getFailLinkObj() {
  let name = '';
  let link = '';
  const curState = cvData?.govData.state || RegistState.unRegist;
  switch (curState) {
    // case RegistState.undo:
    // case RegistState.transfer:
    //   name = i18nGet('常见问题');
    //   link = faqUrl;
    //   break;
    case RegistState.sendFail:
      name = i18nGet('点击查看处理方案');
      link = dealUrl;
    default:
      break;
  }
  return {
    name,
    link,
  };
}

// 登记N天后未审核通过
function getNDaysLaterTips() {
  // let tips = '';

  // if (isRegisted() && isNDaysLater(cvData?.regTime, cvData?.systemTime, 3)) {
  //   tips = i18nGet('查看我的审核进度, 点击此处去');
  // }
  // return tips;
  return i18nGet('审核进度请以政府通知为准');
}

// export function getStepType(s: RegistState): string {
//   const curState = cvData?.state || RegistState.unRegist;
//   if (s < curState) {
//     return 'done';
//   }
//   if (s === curState) {
//     return 'ing';
//   }
//   return 'next';
// }


/**
 * 政府消费券派券后失效
 */
export function isGovCVInvalidAfterSend(cvId?: string) {
  const voucherItem = getData(cvId);
  const state = voucherItem ? voucherItem.cvState : cvData?.govData.state;
  const isInvalid = state === RegistState.sendUndo
    || state === RegistState.sendTransfer;

  return isInvalid;
}

/** 政府消费券派券后撤销 */
export function isGovCvSendUndo(cvId?: string) {
  const voucherItem = getData(cvId);
  const state = voucherItem ? voucherItem.cvState : cvData?.govData.state;
  return state === RegistState.sendUndo;
}
/** 政府消费券派券后转移 */
export function isGovCvSendTransfer(cvId: string) {
  const state = getState(cvId);
  return state === RegistState.sendTransfer;
}

/**
 * 券过期
 */
export function isOverdue(cvId: string) {
  const voucherItem = getData(cvId);
  const state = voucherItem ? voucherItem.cvState : cvData?.govData.state;
  return state === RegistState.overdue;
}

/**
 * 政府消费券派券前失效
 */
export function isGovCVInvalidBeforeSend() {
  const isInvalid = cvData?.govData.state === RegistState.undo
    || cvData?.govData.state === RegistState.transfer;
  // || cvData?.state === RegistState.sendFail;
  return isInvalid;
}

/**
 * 是否首次已登记
 * 只弹出一次，前端localstorage控制是否弹出过
 */
export function isGovCVFirstRegisted() {
  let flag = false;
  if (isRegisted()) {
    const storeFlag = localStorage.getItem(CStorageKeyFirstRegisted);
    if (!storeFlag) {
      flag = true;
      localStorage.setItem(CStorageKeyFirstRegisted, '1');
    }
  }
  return flag;
}

/**
 * 是否首次审核成功
 * 只弹出一次，前端localstorage控制是否弹出过
 */
export function isGovCVFirstApproved() {
  let flag = false;
  if (cvData?.govData.state === RegistState.approved) {
    const storeFlag = localStorage.getItem(CStorageKeyFirstApproved);
    if (!storeFlag) {
      flag = true;
      localStorage.setItem(CStorageKeyFirstApproved, '1');
    }
  }
  return flag;
}

/**
 * 是否首次派券成功
 * 只弹出一次，前端localstorage控制是否弹出过
 */
export function isGovCVFirstSendSucc() {
  let flag = false;
  if (!cvData?.govData.needShowSuccDialog) return flag;
  if (cvData?.govData.state === RegistState.sendSucc) {
    const storeFlag = localStorage.getItem(CStorageKeyGovFirstSendSucc);
    if (!storeFlag) {
      flag = true;
      localStorage.setItem(CStorageKeyGovFirstSendSucc, '1');
    }
  }
  return flag;
}

/**
 * 是否首次派券后转移
 * 只弹出一次，前端localstorage控制是否弹出过
 */
export function isGovCVFirstSendTransfer() {
  let flag = false;
  if (cvData?.govData.state === RegistState.sendTransfer) {
    const storeFlag = localStorage.getItem(CStorageKeyGovFirstSendTransfer);
    if (!storeFlag) {
      flag = true;
      localStorage.setItem(CStorageKeyGovFirstSendTransfer, '1');
    }
  }
  return flag;
}

/**
 * 是否首次派券后撤销
 * 只弹出一次，前端localstorage控制是否弹出过
 */
export function isGovCVFirstSendUndo() {
  let flag = false;
  if (cvData?.govData.state === RegistState.sendUndo) {
    const storeFlag = localStorage.getItem(CStorageKeyGovFirstSendUndo);
    if (!storeFlag) {
      flag = true;
      localStorage.setItem(CStorageKeyGovFirstSendUndo, '1');
    }
  }
  return flag;
}

/**
 * 是否扭蛋活动弹窗
 * 只弹出一次，前端localstorage控制是否弹出过
 */
export function isEggActFirst() {
  let flag = false;
  let storeFlagState = '1';
  if (cvData?.govData.state === RegistState.approved) {
    storeFlagState = localStorage.getItem(CStorageKeyFirstApproved) || '';
  } else if (cvData?.govData.state === RegistState.sendSucc) {
    storeFlagState = localStorage.getItem(CStorageKeyGovFirstSendSucc) || '';
  }
  const storeFlag = localStorage.getItem(EggActFirstkey);
  if (!!storeFlagState && !storeFlag) {
    flag = true;
    localStorage.setItem(EggActFirstkey, '1');
  }
  return flag;
}

/*
 * 商户礼包使用：消费券状态是否处于2，6，9(去掉了5)
 * 2-审核通过，6-消费券使用中，9-消费券已过期
 */
export function isGovCVUseForMerchantPackage() {
  return [
    RegistState.approved,
    RegistState.sendSucc,
    RegistState.overdue,
  ].includes(cvData?.govData.state as any);
}


/**
 * 获取倒计时时间
 *  返回结果如：6天03:31:28
 */
export function getCountdown(nowTime, endTime) {
  const leftTime = endTime - nowTime; // 计算剩余的毫秒数
  const date = {
    days: '0',
    hours: '00',
    minutes: '00',
    seconds: '00',
    isEnd: false,
  };
  if (leftTime > 0) {
    Object.assign(date, {
      days: Math.floor(leftTime / 1000 / 60 / 60 / 24), // 计算剩余的天数
      hours: prefixZero((leftTime / 1000 / 60 / 60) % 24), // 计算剩余的小时
      minutes: prefixZero((leftTime / 1000 / 60) % 60), // 计算剩余的分钟
      seconds: prefixZero((leftTime / 1000) % 60), // 计算剩余的秒数
    });
  } else {
    date.isEnd = true;
  }
  return date;
}

/**
 * 给时间数组前面补零
 *  @param {number} num 时间数字
 */
function prefixZero(num) {
  const val = Number.parseInt(num, 10);
  return `${val < 10 ? '0' : ''}${val}`;
}

export function setData(data) {
  cvData = data;
}


